// ── useRealtimeWitness ────────────────────────────────────────────────────
import { useRef, useState, useCallback, useEffect } from "react";

const REALTIME_MODEL = "gpt-4o-mini-realtime-preview-2024-12-17";
// Voces válidas según OpenAI Realtime API (Mayo 2026)
const VOICES_M = ["ash", "echo", "ballad", "verse", "cedar"];
const VOICES_F = ["coral", "sage", "shimmer", "marin"];

function nameHash(name) {
  let h = 0;
  for (let i = 0; i < name.length; i++) h = (h * 31 + name.charCodeAt(i)) & 0xffff;
  return h;
}

export function pickRealtimeVoice(tipo, genero, nombre) {
  const pool = genero === "f" ? VOICES_F : VOICES_M;
  return pool[nombre ? nameHash(nombre) % pool.length : 0];
}

const VOCAL_INSTRUCTIONS = {
  cooperativo:  "Habla con calma y claridad. Ritmo normal, sin dudas.",
  nervioso:     "Habla con inseguridad. Titubea con 'eh...' o 'bueno...'. Ritmo irregular.",
  hostil:       "Habla cortante y seco. Respuestas muy breves. Tono defensivo.",
  evasivo:      "Habla despacio con rodeos. Tono suave pero esquivo.",
  tecnico:      "Habla con precisión y ritmo constante. Tono neutro y profesional.",
  dramatico:    "Habla con emoción visible. Énfasis en palabras clave.",
  seguro:       "Habla con firmeza total. Sin titubeos. Tono claro y directo.",
  desconfiado:  "Habla despacio con cautela. Pausas antes de responder.",
};

function buildRealtimeInstructions(systemPrompt, temperamento) {
  const vocal = VOCAL_INSTRUCTIONS[temperamento] || VOCAL_INSTRUCTIONS.cooperativo;
  return `INSTRUCCIONES DE VOZ (prioridad máxima): ${vocal}
IMPORTANTE: Máximo 2-3 oraciones. Habla como persona real.

${systemPrompt}`;
}

export function useRealtimeWitness(callbacks) {
  const [isConnected, setIsConnected] = useState(false);
  const [isSpeaking,  setIsSpeaking]  = useState(false);

  const cbRef = useRef(callbacks);
  useEffect(() => { cbRef.current = callbacks; });

  const pcRef              = useRef(null);
  const dcRef              = useRef(null);
  const streamRef          = useRef(null);
  const isSpeakingRef      = useRef(false);
  const witnessTypeRef     = useRef("testigo");
  const responseDoneCbRef  = useRef(null);
  const accumulatedTextRef = useRef("");
  const connectedRef       = useRef(false);
  const audioEndedCbRef    = useRef(null);
  const connectIdRef       = useRef(0);

  // Audio element persistente
  const audioElRef = useRef(null);
  useEffect(() => {
    const el = document.createElement("audio");
    el.autoplay = true;
    el.style.display = "none";
    document.body.appendChild(el);
    audioElRef.current = el;
    return () => {
      try { el.pause(); el.srcObject = null; document.body.removeChild(el); } catch (_) {}
    };
  }, []);

  const markSilent = useCallback(() => {
    isSpeakingRef.current = false;
    setIsSpeaking(false);
    cbRef.current?.onSpeakingChange?.(false, "");
    if (audioEndedCbRef.current) {
      const cb = audioEndedCbRef.current;
      audioEndedCbRef.current = null;
      cb();
    }
  }, []);

  const sendEvent = useCallback((event) => {
    if (dcRef.current?.readyState === "open") {
      dcRef.current.send(JSON.stringify(event));
    }
  }, []);

  const enableVAD = useCallback(() => {
    sendEvent({
      type: "session.update",
      session: {
        turn_detection: {
          type: "server_vad",
          threshold: 0.5,
          prefix_padding_ms: 300,
          silence_duration_ms: 600,
        },
      },
    });
  }, [sendEvent]);

  const disableVAD = useCallback(() => {
    sendEvent({
      type: "session.update",
      session: { turn_detection: null },
    });
  }, [sendEvent]);

  const closeConnection = useCallback(() => {
    connectedRef.current = false;
    if (dcRef.current) { try { dcRef.current.close(); } catch (_) {} dcRef.current = null; }
    if (pcRef.current) { try { pcRef.current.close(); } catch (_) {} pcRef.current = null; }
    if (audioElRef.current) {
      try { audioElRef.current.pause(); audioElRef.current.srcObject = null; } catch (_) {}
    }
    markSilent();
    if (responseDoneCbRef.current) { responseDoneCbRef.current(""); responseDoneCbRef.current = null; }
    setIsConnected(false);
  }, [markSilent]);

  const disconnect = useCallback(() => {
    connectIdRef.current += 1;
    closeConnection();
    if (streamRef.current) {
      streamRef.current.getTracks().forEach(t => t.stop());
      streamRef.current = null;
    }
  }, [closeConnection]);

  const muteMic   = useCallback(() => { streamRef.current?.getAudioTracks().forEach(t => { t.enabled = false; }); }, []);
  const unmuteMic = useCallback(() => { streamRef.current?.getAudioTracks().forEach(t => { t.enabled = true;  }); }, []);

  const waitForConnection = useCallback(async (maxMs = 5000) => {
    let elapsed = 0;
    while (!connectedRef.current && elapsed < maxMs) {
      await new Promise(r => setTimeout(r, 100));
      elapsed += 100;
    }
    return connectedRef.current;
  }, []);

  // ── FIX BUG 3 ──────────────────────────────────────────────────────────
  // Polling-based en vez de callback. Más robusto: no depende del timing
  // exacto de registro del callback, y agrega un buffer post-silencio para
  // que el audio del WebRTC realmente termine de drenarse en el cliente.
  const waitUntilSilent = useCallback(async (maxMs = 30000) => {
    const start = Date.now();
    while (isSpeakingRef.current && Date.now() - start < maxMs) {
      await new Promise(r => setTimeout(r, 100));
    }
    // Buffer post-silencio: 250 ms para drenar el último chunk del WebRTC
    await new Promise(r => setTimeout(r, 250));
  }, []);

  const handleEvent = useCallback((evt) => {
    const wType = witnessTypeRef.current;
    switch (evt.type) {

      case "conversation.item.created":
        if (
          evt.item?.role === "user" &&
          evt.item?.content?.[0]?.type === "input_audio"
        ) {
          cbRef.current?.onUserSpeechDetected?.();
        }
        break;

      case "conversation.item.input_audio_transcription.completed":
        if (evt.transcript?.trim()) cbRef.current?.onUserTranscript?.(evt.transcript.trim());
        break;

      // FIX BUG 3: usar output_audio_buffer.started como señal canónica
      // de inicio (más preciso que response.created — este último fija
      // isSpeaking en cuanto empieza la GENERACIÓN, no la reproducción)
      case "output_audio_buffer.started":
        if (!isSpeakingRef.current) {
          isSpeakingRef.current = true;
          setIsSpeaking(true);
          cbRef.current?.onSpeakingChange?.(true, wType);
        }
        break;

      case "response.created":
        accumulatedTextRef.current = "";
        if (audioElRef.current && audioElRef.current.paused) {
          audioElRef.current.play().catch(() => {});
        }
        // Mantener compat: marcar speaking aquí también, por si
        // output_audio_buffer.started no llega en alguna versión
        if (!isSpeakingRef.current) {
          isSpeakingRef.current = true;
          setIsSpeaking(true);
          cbRef.current?.onSpeakingChange?.(true, wType);
        }
        break;

      case "response.audio_transcript.delta":
        if (evt.delta) {
          accumulatedTextRef.current += evt.delta;
          cbRef.current?.onWitnessTextDelta?.(evt.delta);
        }
        break;

      case "response.audio_transcript.done": {
        const fullText = evt.transcript?.trim() || accumulatedTextRef.current.trim();
        if (fullText) cbRef.current?.onWitnessDone?.(fullText);
        break;
      }

      case "response.done": {
        const savedText = accumulatedTextRef.current.trim();
        accumulatedTextRef.current = "";
        if (responseDoneCbRef.current) {
          const cb = responseDoneCbRef.current;
          responseDoneCbRef.current = null;
          cb(savedText);
        }
        break;
      }

      // ── FIX BUG 3 ────────────────────────────────────────────────────
      // output_audio_buffer.stopped es un evento del SERVIDOR: indica que
      // OpenAI terminó de mandar audio, NO que el cliente terminó de
      // reproducirlo. Entre el evento y el silencio real en los parlantes
      // hay 400-700 ms de audio drenándose en el buffer del WebRTC.
      // 800 ms cubre el peor caso típico.
      case "output_audio_buffer.stopped":
        setTimeout(() => markSilent(), 800);
        break;

      case "error":
        if (responseDoneCbRef.current) { const cb = responseDoneCbRef.current; responseDoneCbRef.current = null; cb(""); }
        markSilent();
        break;

      default: break;
    }
  }, [markSilent]);

  const connect = useCallback(async (witness, systemPrompt) => {
    connectIdRef.current += 1;
    const myId = connectIdRef.current;
    closeConnection();

    const voice        = pickRealtimeVoice(witness.tipo, witness.genero, witness.nombre);
    const instructions = buildRealtimeInstructions(systemPrompt, witness.temperamento);
    witnessTypeRef.current = witness.tipo;

    const isStale = () => connectIdRef.current !== myId;

    try {
      const tokenRes = await fetch("/api/realtime-token", {
        method: "POST",
        headers: { "Content-Type": "application/json" },
        body: JSON.stringify({ voice, instructions }),
      });
      if (isStale()) return;
      if (!tokenRes.ok) throw new Error(`Token ${tokenRes.status}: ${await tokenRes.text()}`);
      const { client_secret } = await tokenRes.json();
      if (isStale()) return;

      const pc = new RTCPeerConnection();
      pcRef.current = pc;

      const audioEl = audioElRef.current;
      if (audioEl) {
        pc.ontrack = (e) => {
          audioEl.srcObject = e.streams[0];
          audioEl.play().catch(() => {});
          audioEl.onended = () => { if (!isStale()) markSilent(); };
        };
      }

      let stream = streamRef.current;
      if (!stream || stream.getTracks().some(t => t.readyState === "ended")) {
        stream = await navigator.mediaDevices.getUserMedia({ audio: true });
        if (isStale()) { stream.getTracks().forEach(t => t.stop()); return; }
        if (streamRef.current) streamRef.current.getTracks().forEach(t => t.stop());
        streamRef.current = stream;
      }

      if (isStale() || pc.signalingState === "closed") return;

      stream.getTracks().forEach(track => {
        if (pc.signalingState !== "closed") pc.addTrack(track, stream);
      });

      if (isStale()) return;

      const dc = pc.createDataChannel("oai-events");
      dcRef.current = dc;
      dc.onopen    = () => { if (!isStale()) { connectedRef.current = true; setIsConnected(true); } };
      dc.onclose   = () => {
        if (!isStale()) {
          connectedRef.current = false; setIsConnected(false); markSilent();
          if (responseDoneCbRef.current) { responseDoneCbRef.current(""); responseDoneCbRef.current = null; }
        }
      };
      dc.onmessage = (e) => { if (!isStale()) try { handleEvent(JSON.parse(e.data)); } catch (_) {} };

      const offer = await pc.createOffer();
      if (isStale()) return;
      await pc.setLocalDescription(offer);
      if (isStale()) return;

      const sdpRes = await fetch(
        `https://api.openai.com/v1/realtime?model=${REALTIME_MODEL}`,
        { method:"POST", headers:{ "Authorization":`Bearer ${client_secret}`, "Content-Type":"application/sdp" }, body: offer.sdp }
      );
      if (isStale()) return;
      if (!sdpRes.ok) throw new Error(`SDP ${sdpRes.status}: ${await sdpRes.text()}`);
      await pc.setRemoteDescription({ type:"answer", sdp: await sdpRes.text() });

    } catch (err) {
      if (!isStale()) { closeConnection(); throw err; }
    }
  }, [closeConnection, handleEvent, markSilent]);

  const sendWitnessQuestion = useCallback(async (questionText) => {
    const ready = await waitForConnection(5000);
    if (!ready) return "";
    return new Promise((resolve) => {
      responseDoneCbRef.current = resolve;
      accumulatedTextRef.current = "";
      if (dcRef.current?.readyState === "open") {
        dcRef.current.send(JSON.stringify({ type:"conversation.item.create", item:{ type:"message", role:"user", content:[{ type:"input_text", text:questionText }] } }));
        dcRef.current.send(JSON.stringify({ type:"response.create" }));
      } else { resolve(""); }
    });
  }, [waitForConnection]);

  const interrupt = useCallback(() => {
    if (dcRef.current?.readyState === "open") dcRef.current.send(JSON.stringify({ type:"response.cancel" }));
    if (audioElRef.current) { try { audioElRef.current.pause(); } catch (_) {} }
    markSilent();
    if (responseDoneCbRef.current) { responseDoneCbRef.current(""); responseDoneCbRef.current = null; }
  }, [markSilent]);

  useEffect(() => { return () => disconnect(); }, [disconnect]);

  return {
    connect, disconnect, interrupt,
    sendWitnessQuestion, waitForConnection, waitUntilSilent,
    muteMic, unmuteMic, enableVAD, disableVAD,
    isConnected, isSpeaking,
  };
}
